/**
 * One row per chapter (brief §progress, learning-evidence design §2.2c–d):
 * the recent answer accuracy bar (greyed + "ej bedömd" when the chapter has no
 * attempts yet), a thin "Underlag" bar for chapter confidence, and the
 * coverage line "{practised} av {total} ämnen övade", expanding to a topic
 * table.
 *
 * On the Progress page the course map carries the bars and the coverage line
 * in its own rows (`ChapterSummary`, pedagogy spec §3.3.5 item 3) and this
 * list shrinks to the per-chapter disclosures that open the topic tables,
 * outside the map's grid (`variant="tables"`).
 */
import { useState } from "react";
import { ChevronDown, ChevronRight, Minus, TrendingDown, TrendingUp } from "lucide-react";
import { useLocalizedText } from "@client/components/Localized";
import { Card, ProgressBar, Table } from "@client/components/ui";
import { useLang, useT } from "@client/i18n";
import { formatPercent, formatRelativeDay } from "@client/lib/format";
import type { Topic } from "@shared/types";
import type { ChapterDto, ChapterProgress, Trend } from "./types";

/** `full`: bars, coverage and the disclosure per chapter. `tables`: the disclosures only. */
type Variant = "full" | "tables";

/** "1. Relativitet": the chapter's number and title, or "Moment 1" before the titles load. */
export function useChapterTitle(): (chapter: number, info: ChapterDto | undefined) => string {
  const t = useT();
  const localize = useLocalizedText();
  return (chapter, info) =>
    info ? `${chapter}. ${localize(info.title)}` : `${t("progress.chapters.chapter")} ${chapter}`;
}

function TrendIcon({ trend }: { trend: Trend }) {
  const t = useT();
  if (trend === "up") {
    return (
      <TrendingUp
        className="h-4 w-4 text-green-600 dark:text-green-400"
        aria-label={t("progress.trend.up")}
      />
    );
  }
  if (trend === "down") {
    return (
      <TrendingDown
        className="h-4 w-4 text-red-600 dark:text-red-400"
        aria-label={t("progress.trend.down")}
      />
    );
  }
  return (
    <Minus
      className="h-4 w-4 text-ink-400 dark:text-ink-500"
      aria-label={t("progress.trend.flat")}
    />
  );
}

/**
 * The chapter's numbers: the accuracy bar ("ej bedömd" and dimmed while there is nothing to
 * show), the thin "Underlag" bar, and the coverage line. Presentational, so it sits outside
 * any toggle button (bug 12, C8): a <button> may hold only phrasing content in the
 * accessibility tree, and nesting the bars inside it hid them from screen readers.
 */
export function ChapterSummary({ chapter }: { chapter: ChapterProgress }) {
  const t = useT();
  const { lang } = useLang();
  const touched = chapter.attempts > 0;
  return (
    <>
      {touched ? (
        <ProgressBar
          value={chapter.mastery}
          tone="accent"
          label={t("progress.chapters.masteryLabel")}
          caption={formatPercent(chapter.mastery, lang)}
          valueText={formatPercent(chapter.mastery, lang)}
        />
      ) : (
        // Label/caption sit outside the dimmed element so only the bar
        // track is greyed out — axe flagged 1.93:1 contrast when
        // opacity-40 faded the text along with the bar.
        <div className="flex flex-col gap-1">
          <div className="flex items-baseline justify-between text-xs text-ink-600 dark:text-ink-400">
            <span>{t("progress.chapters.masteryLabel")}</span>
            <span className="tabular-nums">{t("progress.chapters.notRated")}</span>
          </div>
          <div className="opacity-40">
            <ProgressBar
              value={0}
              tone="accent"
              ariaLabel={`${t("progress.chapters.masteryLabel")}: ${t("progress.chapters.notRated")}`}
              valueText={t("progress.chapters.notRated")}
            />
          </div>
        </div>
      )}
      {touched && (
        <ProgressBar
          value={chapter.confidence}
          tone="muted"
          size="sm"
          ariaLabel={t("progress.chapters.evidenceLabel")}
          valueText={formatPercent(chapter.confidence, lang)}
          // B3: the thin bar had only an accessible name — sighted students saw an
          // unexplained second bar. A small visible caption explains it without
          // adding a full label row (`ProgressBar`'s own API, no bar-specific change).
          // N1: the caption repeats exactly what `ariaLabel` + `valueText` already
          // announce on the bar itself, so it's `captionHidden` from assistive tech
          // to avoid a screen reader hearing "Underlag: 4 %" spoken twice in a row.
          caption={`${t("progress.chapters.evidenceLabel")}: ${formatPercent(chapter.confidence, lang)}`}
          captionHidden
          className="mt-1"
        />
      )}
      {chapter.topicsTotal > 0 && (
        <p className="mt-1 text-xs text-ink-600 dark:text-ink-400">
          {t("progress.chapters.coverage", {
            practised: chapter.topicsPractised,
            total: chapter.topicsTotal,
          })}
        </p>
      )}
    </>
  );
}

function ChapterRow({
  chapter,
  chapterInfo,
  topicNames,
  variant,
}: {
  chapter: ChapterProgress;
  chapterInfo: ChapterDto | undefined;
  topicNames: Map<string, Topic>;
  variant: Variant;
}) {
  const t = useT();
  const { lang } = useLang();
  const localize = useLocalizedText();
  const chapterTitle = useChapterTitle();
  const [expanded, setExpanded] = useState(false);
  const title = chapterTitle(chapter.chapter, chapterInfo);

  const toggle = (
    <button
      type="button"
      onClick={() => setExpanded((v) => !v)}
      className="flex w-full items-center gap-3 text-left"
      aria-expanded={expanded}
    >
      {expanded ? (
        <ChevronDown className="h-4 w-4 shrink-0 text-ink-400" />
      ) : (
        <ChevronRight className="h-4 w-4 shrink-0 text-ink-400" />
      )}
      <span className="w-full text-left font-medium text-ink-800 dark:text-ink-100">{title}</span>
    </button>
  );

  const table = expanded && (
    <div className="mt-4">
      <Table
        columns={[
          {
            key: "topic",
            header: t("progress.topics.table.topic"),
            cell: (row: ChapterProgress["topics"][number]) => {
              const meta = topicNames.get(row.topicId);
              return meta ? localize(meta.name) : row.topicId;
            },
          },
          {
            key: "state",
            header: t("progress.topics.table.state"),
            cell: (row) => (row.state ? t(`progress.map.state.${row.state}`) : "—"),
          },
          {
            key: "mastery",
            header: t("progress.topics.table.mastery"),
            align: "right",
            cell: (row) =>
              row.rated ? formatPercent(row.mastery, lang) : t("progress.topics.notRated"),
          },
          {
            key: "attempts",
            header: t("progress.topics.table.attempts"),
            align: "right",
            cell: (row) => row.attempts,
          },
          {
            key: "lastAt",
            header: t("progress.topics.table.lastPractised"),
            cell: (row) =>
              row.lastAt === null
                ? t("progress.topics.neverPractised")
                : formatRelativeDay(row.lastAt, lang),
          },
          {
            key: "trend",
            header: t("progress.topics.table.trend"),
            align: "center",
            cell: (row) => <TrendIcon trend={row.trend} />,
          },
        ]}
        rows={chapter.topics}
        rowKey={(row) => row.topicId}
        empty={<p className="text-sm text-ink-600 dark:text-ink-400">{t("common.nothingHere")}</p>}
      />
    </div>
  );

  if (variant === "tables") {
    return (
      <div className="mb-2 rounded-lg border border-ink-200 p-3 dark:border-ink-800">
        {toggle}
        {table}
      </div>
    );
  }
  return (
    <Card compact className="mb-3">
      {toggle}
      <div className="pl-7 mt-2">
        <ChapterSummary chapter={chapter} />
      </div>
      {table}
    </Card>
  );
}

export function ChaptersSection({
  chapters,
  chapterInfos,
  topics,
  variant = "full",
}: {
  chapters: ChapterProgress[];
  chapterInfos: ChapterDto[];
  topics: Topic[];
  variant?: Variant;
}) {
  const t = useT();
  const chapterInfoByNumber = new Map(chapterInfos.map((c) => [c.number, c]));
  const topicNames = new Map(topics.map((topic) => [topic.id, topic]));

  return (
    <section>
      {variant === "full" ? (
        <h2 className="mb-3 font-display text-lg text-ink-900 dark:text-ink-50">
          {t("progress.chapters.title")}
        </h2>
      ) : (
        <h3 className="mb-2 mt-4 text-sm font-medium text-ink-700 dark:text-ink-200">
          {t("progress.map.showTable")}
        </h3>
      )}
      {chapters.length === 0 ? (
        <p className="text-sm text-ink-600 dark:text-ink-400">{t("common.nothingHere")}</p>
      ) : (
        chapters.map((chapter) => (
          <ChapterRow
            key={chapter.chapter}
            chapter={chapter}
            chapterInfo={chapterInfoByNumber.get(chapter.chapter)}
            topicNames={topicNames}
            variant={variant}
          />
        ))
      )}
    </section>
  );
}

export default ChaptersSection;
